// Recognise a request to move a campaign's flight start/end date to a stated
// date ("Can you change the start date on the iScreeningRoom campaign to
// 10/15"). PARITY: byte-identical between client and superadmin.
//
// Used only to keep LOCAL routing (planner-revision navigation, voice action
// shortcuts) from swallowing the request: it must reach /api/eden/ask, where
// the backend resolves the campaign and answers with a governed proposal or a
// verified blocker. This module decides nothing about permission or validity.

const MONTH = "(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\\.?";
const DATE =
  "(?:\\d{4}-\\d{1,2}-\\d{1,2}|\\d{1,2}[/-]\\d{1,2}(?:[/-]\\d{2,4})?" +
  `|${MONTH}\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,?\\s+\\d{4})?` +
  `|\\d{1,2}(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH}(?:,?\\s+\\d{4})?)`;
const EDGE = /\b(?:flight\s+(?:start|end)(?:\s+date)?|(?:start|end)(?:ing)?\s+(?:date|day))\b/i;
// One polite phrase at a time (anchored loop, no repeated group): linear.
const POLITE_LEAD =
  /^(?:please|pls|now|just|kindly|eden,?|hey\s+eden,?|ok(?:ay)?,?|(?:can|could|would|will)\s+(?:you|we)|i\s+(?:need|want|would\s+like)\s+(?:you\s+)?to|let'?s|go\s+ahead\s+and)\s+/i;
const VERB = /^(?:change|move|set|update|push|shift|reschedule|adjust|edit|bump|correct|fix)\b/i;
// The target date ends its clause: "on the 10/15 report" is not a target.
const TARGET = new RegExp(
  `\\b(?:to|on|for|as)\\s+(?:the\\s+)?${DATE}(?=\\s*(?:$|[.?!,;]|\\b(?:and|then|instead|please|thanks|thank\\s+you|if|so|asap)\\b))`,
  "i"
);

export function looksLikeCampaignScheduleEditRequest(message) {
  const text = String(message || "").replace(/\s+/g, " ").trim();
  if (!text || text.length > 400) return false;
  let body = text;
  for (let i = 0; i < 8; i += 1) {
    const lead = body.match(POLITE_LEAD);
    if (!lead) break;
    body = body.slice(lead[0].length);
  }
  return VERB.test(body) && EDGE.test(body) && TARGET.test(body);
}

// A QUESTION about a campaign's dates ("When does the X campaign start?",
// "What's the start date?", "Show me when the YouTube campaign starts",
// "Launch date for the X campaign") is answered by Eden from the campaign
// record, so no local page shortcut may take it. Only questions count: an
// instruction that mentions a date, however politely ("Can you edit the
// start date on the Yahoo campaign?", "Show me the planner so I can fix the
// end date"), keeps its local planner and navigation handling. Bounded,
// linear.
const LEAD_WORDS = "(?:(?:hey|hi|ok(?:ay)?|so|and|eden|please)[\\s,:]+){0,4}";
const POLITE = "(?:(?:can|could|would|will)\\s+you\\s+(?:please\\s+)?)?";
const DATE_NOUN = "(?:start|end|flight|launch|go[-\\s]?live)\\s+(?:date|dates|day)\\b";
const DATE_TOPIC =
  /\bwhen\b.{0,160}\b(?:start|starts|starting|begin|begins|end|ends|ending|finish|finishes|launch|launches|go(?:es)?\s+live|kicks?\s?off)\b|\b(?:start|end|flight|launch|go[-\s]?live)\s+dates?\b/i;
const QUESTION_LEAD =
  /^(?:(?:hey|hi|ok(?:ay)?|so|and|eden|please)[\s,:]+){0,4}(?:(?:can|could|would|will)\s+you\s+(?:please\s+)?(?:tell|show|remind|give)\s+me\s+(?:of\s+|about\s+)?|do\s+you\s+know\s+|tell\s+me\s+(?:about\s+)?|remind\s+me\s+(?:of\s+|about\s+)?|show\s+me\s+|give\s+me\s+)?(?:when|what|whats|which|how\s+long)\b/i;
// "Show me / Give me / Tell me (about) / Remind me (of) / Check / Confirm /
// Look up" followed by the date itself (or "when"), with no other clause in
// between ("show me the planner so I can …" is not one).
const INFO_REQUEST = new RegExp(
  `^${LEAD_WORDS}${POLITE}(?:remind\\s+me(?:\\s+(?:of|about))?|tell\\s+me(?:\\s+about)?|show\\s+me|give\\s+me|check|confirm|look\\s+up)\\s+(?:when\\b|(?:(?!(?:so|and|then|to|i|we|can|could|while|but)\\b)[^\\s,;.]+\\s+){0,6}?${DATE_NOUN})`,
  "i"
);
const INFO_LEAD_START = new RegExp(`^${LEAD_WORDS}${POLITE}(?:remind|tell|show|give|check|confirm|look)\\b`, "i");
// "Start date for X", "Launch date for X", "Go-live date for X".
const DATE_NOUN_START = new RegExp(`^${LEAD_WORDS}(?:the\\s+)?${DATE_NOUN}`, "i");
const ACTION_LEAD = new RegExp(
  `^${LEAD_WORDS}${POLITE}(?:take|open|go(?![-\\s]?live\\s+dates?\\b)|navigate|bring|edit|update|revise|change|move|set|push|shift|adjust|reschedule|fix|correct|create|build|make|start(?!\\s+(?:and\\s+end\\s+)?dates?\\b)|launch(?!\\s+dates?\\b)|pause|stop|cancel|close|back|return|exit|enter|end(?!\\s+dates?\\b)|leave|delete|remove)\\b`,
  "i"
);

export function looksLikeCampaignScheduleQuestion(message) {
  const text = String(message || "").replace(/\s+/g, " ").trim();
  if (!text || text.length > 400) return false;
  if (!DATE_TOPIC.test(text) || ACTION_LEAD.test(text)) return false;
  if (QUESTION_LEAD.test(text) || INFO_REQUEST.test(text) || DATE_NOUN_START.test(text)) return true;
  // Any other sentence with a date in it that ends in "?" is a question,
  // unless it opens as a request to show or give something else.
  return /\?$/.test(text) && !INFO_LEAD_START.test(text);
}

// Is a reply a command for this page rather than an answer to Eden's
// question? "back" on its own and anything about dictation always are. A
// reply that opens with a navigation phrase ("Show me / Take me to / Go to /
// Open / Pull up / Bring up / Go back to / Return to / Back to …") is one when
// the rest names a page: `resolvesToPage(rest)` decides, and without it the
// phrase alone counts. So "Return to the dashboard" is a command while a
// campaign named "Return to Seoul" or "Back to School" can be the answer.
const BARE_COMMAND = new RegExp(`^${LEAD_WORDS}(?:(?:go\\s+)?back|return|previous(?:\\s+page)?)$|\\bdictation\\b`, "i");
const NAVIGATION_PHRASE = new RegExp(
  `^${LEAD_WORDS}${POLITE}(?:show\\s+me|go\\s+back\\s+to|back\\s+to|return\\s+to|go\\s+to|pull\\s+up|bring\\s+up|bring\\s+me(?:\\s+to)?|take\\s+me(?:\\s+to)?|navigate\\s+to|open)\\b\\s*`,
  "i"
);

export function looksLikeLocalCommand(message, { resolvesToPage = null } = {}) {
  const text = String(message || "").replace(/\s+/g, " ").trim().slice(0, 400);
  if (!text) return false;
  if (BARE_COMMAND.test(text)) return true;
  const phrase = text.match(NAVIGATION_PHRASE);
  if (!phrase) return false;
  const rest = text.slice(phrase[0].length).trim();
  if (!rest || typeof resolvesToPage !== "function") return true;
  return Boolean(resolvesToPage(rest));
}

// The campaigns Eden listed in "Which campaign do you mean? These match: A
// (#1); B (#2, Advertiser). Tell me …": a reply naming one of them answers
// the question, even after a navigation phrase ("Show me YouTube Q4").
const LISTED_NAMES = /(?:These match|most recently updated are):\s*(.{1,1200}?)\.\s+Tell me\b/;

export function listedCampaignNames(replyText) {
  const match = String(replyText || "").slice(0, 2000).match(LISTED_NAMES);
  if (!match) return [];
  return match[1]
    .split(/;\s*/)
    .slice(0, 6)
    .map((item) => item.replace(/\s*\(#\d{1,9}(?:,[^)]*)?\)\s*$/, "").trim().slice(0, 120))
    .filter(Boolean);
}

// The text after a leading navigation phrase ("Show me YouTube Q4" → "YouTube Q4").
export function withoutNavigationPhrase(message) {
  const text = String(message || "").replace(/\s+/g, " ").trim().slice(0, 400);
  return text.replace(NAVIGATION_PHRASE, "").trim();
}

export default looksLikeCampaignScheduleEditRequest;
